import { useContext } from 'react'
import { Helmet } from 'react-helmet-async'
import PropTypes from 'prop-types'

import { faGlobe } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import useBrandGroup, { generateUrls } from '@hooks/useBrandGroup'
import useBrandGroups from '@hooks/useBrandGroups'
import useCurrentUser from '@hooks/useCurrentUser'

import ListBlankState from '@components/ListBlankState'
import PageFilterModule from '@components/PageFilterModule'
import PageHeader from '@components/PageHeader'

import BrandGroupListItem from '../BrandGroupListItem'

const buildPageFilterFields = (isBrandUser) => {
  const style = { marginBottom: 16 }
  const boxProps = { marginBottom: 'large' }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'string',
      label: 'Filter by Title or ID',
    },
    {
      callbacks: {
        shouldRender: () => !isBrandUser,
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'brands',
      },
      key: 'brand_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: state => ({ brand_id: state.brand_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'agencies',
      },
      dependentKey: 'brand_id',
      key: 'agency_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: () => ({ publication_providers: true }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'providers',
      },
      key: 'provider_id',
    },
    {
      callbacks: {
        buildSearchRequestOptions: state => ({ provider_id: state.provider_id }),
      },
      component: 'EntitySelector',
      componentProps: {
        boxProps,
        entityKey: 'publications',
      },
      key: 'publication_id',
    },
  ]
}

const PageContent = (props) => {
  const { pageFilters } = props

  const brandGroupsPayload = useBrandGroups({
    filters: pageFilters,
    performHttpRequests: true,
    requestOptions: {
      include_brand_group_brand: true,
      include_brand_group_members_count: true,
      include_brand_group_member_ids: !!pageFilters.agency_id,
      include_brand_group_provider_ids: !!pageFilters.provider_id,
      include_brand_group_publication_ids: !!pageFilters.publication_id,
    },
  })
  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredBrandGroups,
    filteredBrandGroupsCount,
    hasFilteredBrandGroups,
    loading,
  } = brandGroupsPayload

  const pageContext = useContext(PageContext)
  const {
    callbacks: {
      resetFilters,
      updateFilters,
    },
    defaultFilters,
  } = pageContext

  const { brandGroupsIndexUrl } = generateUrls()

  const { brand, isBrandUser } = useCurrentUser()

  const { callbacks: { addBrandGroup } } = useBrandGroup({ brand_id: brand?.id })

  return (
    <>
      <Helmet>
        <title>Brand Groups | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => addBrandGroup()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="brandGroups"
        tabBarItems={[
          {
            href: brandGroupsIndexUrl,
            icon: faGlobe,
            key: 'brandGroups',
            title: 'Brand Groups',
          },
        ]}
        title="Brand Groups"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredBrandGroups && <ListBlankState />}

            {!loading && hasFilteredBrandGroups && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Brand Groups">
                <Box flexDirection="column">
                  {filteredBrandGroups.map(brandGroup => (
                    <BrandGroupListItem key={brandGroup.id} brandGroup={brandGroup} />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredBrandGroupsCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <PageFilterModule
              callbacks={{ resetFilters, updateFilters }}
              defaultFilters={defaultFilters}
              filterFields={buildPageFilterFields(isBrandUser)}
              pageFilters={pageFilters}
            />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

PageContent.propTypes = {
  pageFilters: PropTypes.object.isRequired,
}

export default PageContent
